import Head from 'next/head';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Box, Column, FlexGrow } from '../../components/Flex';
import { BackgroundButton } from '../../components/Form';
import { Body, HeadingOne, Subheading } from '../../components/Text';
import {
  WHITE_LABEL_ACCENT_COLOR,
  WHITE_LABEL_BRAND,
} from '../../lib/white-label';
import { COLORS } from '../../theme';

// intercom
import { showIntercomWindow } from 'next-intercom';
import { analytics } from '../../lib/analytics';
import Faq from '../../components/svgs/Faq';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';

const Container = styled.div`
  width: 100%;
  max-width: 850px;
  margin-right: auto;
  margin-left: auto;
`;

const FAQContainer = styled(Column)`
  padding: 2rem 2.5rem;
  width: 100%;
  border: 1px solid #cacfe0;
  border-radius: 24px;
  opacity: 1;
  margin-bottom: 2rem;
`;

const FAQQuestion = styled.p`
  margin: 0;
  font: normal normal bold 19px/29px Poppins;
  color: ${COLORS.BLACK};
`;

const FAQAnswer = styled(Body)`
  margin: 1rem 0 0 0;
`;

const ContactUs = styled(Column)`
  background: #ffffff;
  box-shadow: 1rem 1rem 5rem rgba(145, 157, 181, 0.2);
  border-radius: 20px;
  width: 100%;
  max-width: 600px;
  padding: 3rem;
  margin: 8rem auto;
  align-items: center;
  @media only screen and (max-width: 800px) {
    flex-direction: column;
    text-align: center;
  }
`;

function FAQ() {
  const onContact = () => {
    analytics.track('User Clicked FAQ Contact');
    showIntercomWindow();
  };

  useEffect(() => {
    analytics.page(`Tidal FAQ`);
  }, []);

  return (
    <Container>
      <Head>
        <title>FAQ</title>
        <link rel="canonical" href={`https://www.tidalmarket.com/faq`} />
      </Head>
      <HeadingOne>Frequently Asked Questions</HeadingOne>
      <Subheading>Learn more about {WHITE_LABEL_BRAND} Rentals.</Subheading>
      <Box height={20} />
      <FAQContainer>
        <FAQQuestion>Why should I use {WHITE_LABEL_BRAND} Rentals?</FAQQuestion>
        <FAQAnswer>
          {WHITE_LABEL_BRAND} Rentals has built a system that ensures both the
          bot owner and renter are protected. Every day we see reports of rental
          scams. This includes bot owners taking your money without giving you
          access to the bot, refusing to provide key resets, and being slow to
          give crucial announcements regarding the bot. We provide all guides,
          updates, and announcements in a centralized location so you don’t have
          to rely on the bot owner.
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>What sort of scam protections do we provide?</FAQQuestion>
        <FAQAnswer>
          {WHITE_LABEL_BRAND} Rentals promises that any key resets needed will
          happen in a timely manner and any key/relevant information for success
          with your rented bot will be provided. If either of these conditions
          are not met, we will issue a full refund to the customer. When it
          comes to protection for the seller, we ensure that all our guides,
          updates, and announcements are up to date for your renter, while also
          offering 100% coverage for any wrongful chargebacks that may occur.
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>
          What is the difference between {WHITE_LABEL_BRAND} Staff Listings and
          Member Listings?
        </FAQQuestion>
        <FAQAnswer>
          {WHITE_LABEL_BRAND} Staff Listings will always be supplied a staff
          member on the {WHITE_LABEL_BRAND} Team, giving you the extra
          reassurance of a smooth transaction. Depending on the bot, you may
          notice a slight increase in price for {WHITE_LABEL_BRAND} Staff
          Listings. That is because when renting from {WHITE_LABEL_BRAND} Staff,
          you can know for certain that your keys will be reset as quickly as
          possible by our staff members working around the clock.
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>Do you offer any benefits for frequent users?</FAQQuestion>
        <FAQAnswer>
          Returning members are absolutely rewarded! As a seller, you can
          achieve higher ranks. As you increase your seller rank, your platform
          fees will decrease. We offer returning buyers a feeless rental after
          each paid rental. Check your profile tab in the dashboard to learn
          more about our benefits!
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>How do payouts work?</FAQQuestion>
        <FAQAnswer>
          We are using a partnered payment system through Stripe, which allows
          you to have the security of a big provider. Once your rental is over,
          Stripe will automatically send a payout directly to your bank 3-5 days
          after your rental is over. If for some reason your bank is not
          supported by Stripe, you can link your Transferwise account through
          our Stripe platform as well.
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>What do I need to do to rent out my bots?</FAQQuestion>
        <FAQAnswer>
          Listing your bots is simple. All you need to do is create an account,
          link your discord (must be 30 days or older), and link your Stripe
          account. That’s it!
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>I don’t see the bot I want to rent.</FAQQuestion>
        <FAQAnswer>
          We are currently trying to provide the best support we can for the
          most popular bots. Over time we will be adding more bots to our
          system! If there is a bot you think we should add to our site please
          suggest it in our discord!
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>
          If I have a problem with my rental, what should I do?
        </FAQQuestion>
        <FAQAnswer>
          We understand that not every rental is perfect. If you aren’t
          satisfied with your rental experience, please open a ticket in our
          live chat at the bottom right of your screen!. This allows us to give
          you personalized, one on one support to resolve your issue!
        </FAQAnswer>
      </FAQContainer>
      <FAQContainer>
        <FAQQuestion>
          When would {WHITE_LABEL_BRAND} Rentals not provide a refund?
        </FAQQuestion>
        <FAQAnswer>
          {WHITE_LABEL_BRAND} Rentals does not take responsibility if the bot
          you decided to rent does not perform well on a release. We understand
          that it can be frustrating to pay for a rental and not get the result
          you were looking for. If you want to ensure success on a release join
          our discord and reach out to our support staff with any questions you
          have regarding bot performance and setup. To read our full terms of
          service click here.
        </FAQAnswer>
      </FAQContainer>
      <ContactUs>
        <QuestionMarkCircleIcon
          color={WHITE_LABEL_ACCENT_COLOR}
          className="w-12 h-12"
        />
        <div className="p-4 text-lg">
          Couldn't find the answer you were looking for?
        </div>
        <FlexGrow />
        <BackgroundButton onClick={onContact}>Contact Us</BackgroundButton>
      </ContactUs>
    </Container>
  );
}

export async function getStaticProps() {
  return { props: {} };
}

export default FAQ;
